import { memo, useMemo, useCallback } from 'react';
import { Button, Interpolate, TooltipBody } from '@wg/wows-react-uikit';

import { clanIsFull, isClanPreloaded } from '~/helpers/clan';
import { isoToFormattedLocalDate, isoToFormattedLocalTime } from '~/helpers/datetime';
import { t } from '~/helpers/localization';

import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { ClanInvitationMinimal } from '~/Actions/ActionInvites';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { ClanPreloaded } from '~/types/declaration';

interface ButtonJoinClanProps {
  clan: ClanInfoType | ClanPreloaded;
  currentAccount: ICurrentAccountState;
  isEllipsis?: boolean;
  size?: 'isSmaller' | 'isDefault';
  classNameBtnInner?: string;
  onAcceptInvite: (invite: ClanInvitationMinimal) => void;
  onClick?: () => void;
  onSendApplication: (clan: ClanInfoType) => void;
}

const ButtonJoinClan = ({
  clan,
  currentAccount,
  isEllipsis,
  size,
  classNameBtnInner,
  onAcceptInvite,
  onClick,
  onSendApplication,
}: ButtonJoinClanProps) => {
  const isSmaller = size === 'isDefault' ? false : true;

  const viewState = useMemo(() => {
    const isClanDataReady = !isClanPreloaded(clan);

    const isAlreadySentApplication = isClanDataReady ? Object.keys(clan.active_applications).length > 0 : false;

    const rawInvite = isClanDataReady ? (clan.active_invites?.[0] as string | undefined) : undefined;
    const inviteId = rawInvite && rawInvite.length ? parseInt(rawInvite.match(/(\d+)/)?.[0] ?? '', 10) : undefined;

    const hasInvite = Boolean(inviteId);
    const isFull = isClanDataReady ? clanIsFull(clan) : false;

    if (isAlreadySentApplication) {
      return {
        label: t('Заявка отправлена'),
        isDisabled: true,
        tooltipContent: null,
        mode: 'sendApplication',
      };
    }

    if (hasInvite && isFull) {
      return {
        label: t('Вступить в клан'),
        isDisabled: true,
        tooltipContent: <TooltipBody>{t('Вам отправлено приглашение, но в клане сейчас нет мест.')}</TooltipBody>,
        mode: 'acceptInvite',
        inviteId,
      };
    }

    if (currentAccount.inClanCooldownTill) {
      const i18nKey = hasInvite
        ? t('Вы недавно покинули клан. Вы сможете вступить в клан начиная с %(date)s&nbsp;%(time)s.')
        : t('Вы недавно покинули клан. Вы сможете подать заявку в клан начиная с %(date)s&nbsp;%(time)s.');

      return {
        label: t('Вступить в клан'),
        isDisabled: true,
        tooltipContent: (
          <TooltipBody>
            <Interpolate
              useDangerouslySetInnerHTML={true}
              str={i18nKey}
              date={isoToFormattedLocalDate(currentAccount.inClanCooldownTill)}
              time={isoToFormattedLocalTime(currentAccount.inClanCooldownTill)}
              parentTag={'p'}
            />
          </TooltipBody>
        ),
        mode: hasInvite ? 'acceptInvite' : 'sendApplication',
        inviteId,
      };
    }

    if (hasInvite) {
      return {
        label: t('Вступить в клан'),
        isDisabled: false,
        tooltipContent: null,
        mode: 'acceptInvite',
        inviteId,
      };
    }

    return {
      label: t('Вступить в клан'),
      isDisabled: false,
      tooltipContent: null,
      mode: 'sendApplication',
    };
  }, [clan, currentAccount]);

  const handleClick = useCallback(() => {
    if (viewState.isDisabled) {
      return;
    }

    if (viewState.mode === 'acceptInvite' && viewState.inviteId) {
      onAcceptInvite({
        id: viewState.inviteId,
        clan: {
          id: clan.id,
          leveling: !isClanPreloaded(clan) ? clan.leveling : 0,
          tag: clan.tag,
          name: clan.name,
          color: clan.color,
        },
      });
      return;
    }

    onClick?.();
    onSendApplication(clan as ClanInfoType);
  }, [clan, onAcceptInvite, onClick, onSendApplication, viewState]);

  return (
    <Button
      classNameBtnInner={classNameBtnInner}
      isFlat
      isSmaller={isSmaller}
      onClick={handleClick}
      isDisabled={viewState.isDisabled}
      tooltipContent={viewState.tooltipContent}
      isEllipsis={isEllipsis}
      isJustified
    >
      {viewState.label}
    </Button>
  );
};

export default memo(ButtonJoinClan);
